'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/info-tip/styles.css';
import { BottomSheet } from 'BomdisoftComponents/drawers/bottom-sheet';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { InfoOutlinedIcon } from 'BomdisoftComponents/icons/info-outlined';
import { useCoarsePointer } from 'BomdisoftComponents/inputs/field/select/pointer';
import { type BubbleSide, placeBubble } from 'BomdisoftComponents/tooltip/placement';
import { aboutSurfaceT, getLanguageFromLocale } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';

export type InfoTipEntry = {
  /** Exactly what the reader sees on the surface. Pass the SAME element the
   *  surface renders, a `Status` chip most often, so the two match by sight
   *  rather than by position. */
  term: ReactNode;
  description: ReactNode;
};

export type InfoTipSize = 'compact' | 'default' | 'wide' | 'wider';

export type InfoTipProps = {
  /** Names the surface, and becomes the trigger's accessible name as
   *  "About {title}" unless `triggerLabel` overrides it. */
  title: string;
  /** What the surface captures, in one or two sentences. */
  description: ReactNode;
  /** One per thing a reader could misread, including the ones that look
   *  obvious. A column of raw event names is not obvious to someone who did not
   *  write the SDK call. */
  entries?: readonly InfoTipEntry[];
  /** For a surface whose title does not read as a noun after "About". */
  triggerLabel?: string;
  /** How wide the card is. The content is not one shape: two short lines and
   *  seven definitions do not read well at the same measure, and a fixed 316px
   *  wrapped every meaning in a long entry list to three lines. A surface
   *  needing an exact number instead sets `--bomdisoft-info-tip-width` through
   *  `className`. */
  size?: InfoTipSize;
  placement?: BubbleSide;
  className?: string;
  testId?: string;
};

const SHOW_DELAY_MS = 200;
const HIDE_DELAY_MS = 160;

/**
 * The info icon on a card, table or panel, and the card it opens.
 *
 * Every surface that shows a number carries one, because a number with no
 * stated unit, window or baseline is unreadable without the query behind it:
 * Top movers said `$pageview 49 +49` and nothing on screen said what 49 counted
 * or what the +49 was measured against.
 *
 * The trigger carries the accessible name and no `Tooltip` of its own. The
 * frontend rule pairs an icon-only button with a tooltip, but here the thing the
 * button opens IS the explanation, and a hint bubble fighting the card for the
 * same pointer would cover it.
 */
export const InfoTip = ({ title, description, entries, triggerLabel, placement = 'bottom', size = 'default', className, testId }: InfoTipProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(params?.lang as string);
  const id = useId();
  const cardId = `${id}-card`;
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<BubbleSide>(placement);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const coarse = useCoarsePointer();

  const label = triggerLabel ?? aboutSurfaceT[language].replace('{name}', title);

  const show = () => {
    clearTimeout(closeTimer.current);
    clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
  };

  /* Leaving the trigger does not close it at once: the card is meant to be read,
     and the pointer has to cross the gap to reach it. The card cancels this on
     its own enter. */
  const hide = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), HIDE_DELAY_MS);
  };

  const toggle = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    setOpen(previous => !previous);
  };

  useEffect(
    () => () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    },
    [],
  );

  const place = useCallback(() => {
    const card = cardRef.current;
    const trigger = triggerRef.current;
    if (!card || !trigger) return;
    const position = placeBubble(trigger.getBoundingClientRect(), card.getBoundingClientRect(), { height: window.innerHeight, width: window.innerWidth }, placement);
    setSide(position.side);
    card.style.top = `${position.top}px`;
    card.style.left = `${position.left}px`;
  }, [placement]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card || coarse) return undefined;
    if (!open) {
      if (card.matches(':popover-open')) card.hidePopover();
      return undefined;
    }
    if (!card.matches(':popover-open')) card.showPopover();
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.addEventListener('transitionend', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('transitionend', place, true);
    };
  }, [coarse, open, place]);

  // React simulates bubbling for toggle even though the DOM event does not, so
  // a card closing inside a panel would otherwise close the panel too.
  const handleToggle = (event: { newState?: string; target?: EventTarget | null }) => {
    if (event.target !== cardRef.current) return;
    if (event.newState === 'closed') setOpen(false);
  };

  const body = (
    <>
      <h4 className='bomdisoft-info-tip-title'>{title}</h4>
      <p className='bomdisoft-info-tip-description'>{description}</p>
      {entries && entries.length > 0 ? (
        <>
          <hr className='bomdisoft-info-tip-rule' />
          <dl className='bomdisoft-info-tip-entries'>
            {entries.map((entry, index) => (
              <div className='bomdisoft-info-tip-entry' key={index}>
                <dt className='bomdisoft-info-tip-term'>{entry.term}</dt>
                <dd className='bomdisoft-info-tip-meaning'>{entry.description}</dd>
              </div>
            ))}
          </dl>
        </>
      ) : null}
    </>
  );

  return (
    <span className={joinClasses('bomdisoft-info-tip-root', className)}>
      <button aria-controls={cardId} aria-expanded={open} aria-label={label} className='bomdisoft-info-tip-trigger' data-testid={testId} onBlur={hide} onClick={toggle} onFocus={show} onPointerEnter={coarse ? undefined : show} onPointerLeave={coarse ? undefined : hide} ref={triggerRef} type='button'>
        <InfoOutlinedIcon size={15} />
      </button>
      {coarse ? (
        <BottomSheet label={label} onClose={() => setOpen(false)} open={open} testId={testId ? `${testId}-sheet` : undefined}>
          <div className='bomdisoft-info-tip-sheet'>{body}</div>
        </BottomSheet>
      ) : (
        <div className='bomdisoft-info-tip-card' data-side={side} data-size={size} id={cardId} onPointerEnter={() => clearTimeout(closeTimer.current)} onPointerLeave={hide} onToggle={handleToggle} popover='auto' ref={cardRef} role='note'>
          {body}
          <span aria-hidden='true' className='bomdisoft-info-tip-arrow' />
        </div>
      )}
    </span>
  );
};
